import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Select, Spin, Tooltip, message, Button } from 'antd';
import { CopyOutlined, CopyFilled } from '@ant-design/icons'; // Import CopyOutlined icon
import { getQALimitedMessages, postQAEvaluationsSingle } from "../redux/actions/chatActions";
import moment from 'moment';
import ReactMarkdown from "react-markdown";
import DOMPurify from "dompurify";
import { now } from "lodash";

const { Option } = Select;

const TestQandA = () => {
    const permittedApps = useSelector(state => state.chat.permittedApps);
    const dispatch = useDispatch();
    const [localData, setLocalData] = useState([]);
    const [selectedApp, setSelectedApp] = useState('');
    const [isLoading, setIsLoading] = useState(false); // State to track loading
    const useremail = useSelector((state) => state.auth.user.email); // Access the entire user object
    useEffect(() => {
        if (["admin", "write"].includes(permittedApps.admin_tool_role) && permittedApps.genai_apps.length > 0) {
            setSelectedApp(permittedApps.genai_apps[0]);
        }
    }, [permittedApps]);

    useEffect(() => {
        const fetchQAData = async () => {
            if (selectedApp) {
                setIsLoading(true);
                const response = await dispatch(getQALimitedMessages({ AppName: selectedApp, filters: 'None', table_limit_info: 'None' }));
                if (response && response) {
                    setLocalData(response);
                }
                setIsLoading(false);
            }
        };
        fetchQAData();
    }, [dispatch, selectedApp]);
    const [hoveredRow, setHoveredRow] = useState(null);
    const handleValidationChange = async (value, record) => {
        try {
            const userEmail = useremail; // Replace with actual user email
            const validationTime = new Date().toISOString();

            const payload = {
                app_data: {
                    AppName: selectedApp, // The actual application name
                },
                qa_validation_data: {
                    test_id: record.test_id, // Use the current record's test ID
                    validation: value, // This should be a boolean value
                    validator_email: userEmail,
                    validation_time: validationTime
                },
            };

            // Dispatch your action to post the validation
            await dispatch(postQAEvaluationsSingle(payload));

            // Optional: Show a success message

            const response = await dispatch(getQALimitedMessages({ AppName: selectedApp, filters: 'None', table_limit_info: 'None' }));
            if (response && response) {
                setLocalData(response);
            };
            message.success('Validation status updated successfully!');
        } catch (error) {
            console.error("Error posting validation:", error);
            message.error('Failed to update validation status. Please try again.');
        }
    };

    const handleAppChange = async (value) => {
        if (value) {
            dispatch(setSelectedApp(value));
        }
    };

    // Function to copy text to clipboard
    const copyToClipboard = (text) => {
        navigator.clipboard.writeText(text)
            .then(() => {
                message.success('Question copied to clipboard!');
            })
            .catch((error) => {
                console.error('Error copying text: ', error);
                message.error('Failed to copy question.');
            });
    };

    return (
        <div className="adminTable-container">
            {isLoading && (
                <div className="loading-overlay">
                    <Spin tip="Loading data, please wait..." />
                </div>
            )}
            <div style={{ backgroundColor: '#999' }}>
                <h1 style={{ textAlign: 'center', color: 'white', fontFamily: 'Roboto, sans-serif', padding: '10px' }}>
                    Q&A Validation
                </h1>
            </div>          <div style={{ display: 'flex', alignItems: 'center', padding: '10px' }}>
                <span style={{ width: '120px', textAlign: 'left', lineHeight: '32px' }}>Application:</span>
                <Select
                    placeholder="Application"
                    style={{ width: 100 }}
                    value={selectedApp}
                    onChange={handleAppChange}
                >
                    {["admin", "write"].includes(permittedApps.admin_tool_role) &&
                        permittedApps.genai_apps.map((app) => (
                            <Option key={app} value={app}>
                                {app}
                            </Option>
                        ))}
                </Select>

            </div>
            <div className="adminTable-wrapper">
                <table className="adminTable">
                    <thead>
                        <tr>
                            <th style={{ width: 71, fontWeight: 'bold' }}>Test ID</th>
                            <th style={{ minWidth: 100, fontWeight: 'bold' }}>Question</th>
                            <th style={{ minWidth: 0 }}></th>
                            <th style={{ minWidth: 160, fontWeight: 'bold' }}>Answer</th>
                            <th style={{ width: 91, fontWeight: 'bold' }}>User Validation</th>
                        </tr>
                    </thead>
                    <tbody>
                        {Array.isArray(localData) && localData.length > 0 ? (
                            localData.map((record) => (
                                <tr key={record.test_id}>
                                    <td style={{ padding: '0', textAlign: 'center' }}>{record.test_id}</td>
                                    <td style={{ padding: '0', alignItems: 'center' }}>
                                        <span style={{ marginRight: '8px' }}>{record.question}</span>
                                    </td>
                                    <td style={{ padding: '0', textAlign: 'center' }}>
                                    <Tooltip title="Copy Question!">
                                        {hoveredRow === record.test_id ? (
                                            <CopyFilled
                                                onMouseLeave={() => setHoveredRow(null)}
                                                onClick={() => copyToClipboard(record.question)}
                                                style={{ color: '#1890ff', cursor: 'pointer' }} // Style for the icon
                                            />
                                        ) : (
                                            <CopyOutlined
                                                onMouseEnter={() => setHoveredRow(record.test_id)}
                                                onClick={() => copyToClipboard(record.question)}
                                                style={{ color: '#1890ff', cursor: 'pointer' }} // Style for the icon
                                            />
                                        )}
                                    </Tooltip>
                                    </td>


                                    <td>

                                        {/* Sanitize and render the answer safely */}
                                        <ReactMarkdown>
                                            {DOMPurify.sanitize(record.answer)}
                                        </ReactMarkdown>
                                        {record.file_name && record.web_url && ( // Check for file name and URL
                                            <div style={{ marginTop: '8px' }}>
                                                Source:
                                                <a href={record.web_url} className="source_link_anchor" target="_blank" rel="noopener noreferrer">
                                                    {record.file_name}

                                                </a>
                                            </div>
                                        )}
                                    </td>
                                    <td style={{ textAlign: 'center' }}>
                                        <Tooltip
                                            title={
                                                record.validation !== null
                                                    ? `Validated by: ${record.validator_email || 'N/A'} at ${record.validation_time || 'N/A'}`
                                                    : 'Not Validated'
                                            }
                                        >
                                            <Select
                                                value={record.validation === null ? "" : (record.validation ? "Valid" : "Not Valid")}
                                                onChange={(value) => handleValidationChange(value, record)}
                                                style={{ width: 120 }}
                                            >
                                                <Option value="true">Valid</Option>
                                                <Option value="false">Not Valid</Option>
                                            </Select>
                                        </Tooltip>
                                    </td>


                                </tr>
                            ))
                        ) : (
                            <tr>
                                <td colSpan="4" style={{ textAlign: 'center' }}>No data available</td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

export default TestQandA;

